import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { DataSync, DataSyncProperty, Job } from '@/api/types'
import { pushJob } from '@/features/job/queries'

/**
 * How a table is kept in step with something outside this instance.
 *
 * Read from its own endpoint rather than from the copy nested on the table in
 * the sidebar: that one is a summary and carries neither `last_error` nor the
 * connection settings, which are the two things anybody opening this is here
 * for.
 */
export function dataSyncQuery(dataSyncId: number) {
  return queryOptions({
    queryKey: qk.dataSync(dataSyncId),
    queryFn: (): Promise<DataSync> =>
      unwrap(
        getApi().GET('/api/database/data-sync/{data_sync_id}/', {
          params: { path: { data_sync_id: dataSyncId } },
        }),
      ),
  })
}

export function useDataSync(dataSyncId: number) {
  return useQuery(dataSyncQuery(dataSyncId))
}

/**
 * Everything the source offers, whether it is being synced or not.
 *
 * A separate request from the sync itself, because it *asks the source*: a
 * calendar that has stopped answering fails here while the settings still read
 * back perfectly. That is why the properties are their own query rather than
 * part of the sync -- a broken connection should not take the settings form
 * down with it.
 */
export function dataSyncPropertiesQuery(dataSyncId: number) {
  return queryOptions({
    queryKey: qk.dataSyncProperties(dataSyncId),
    // Asked once per dialog: it reaches out over the network to somebody
    // else's server, which is not something to repeat on a window focus.
    staleTime: Infinity,
    retry: false,
    queryFn: (): Promise<DataSyncProperty[]> =>
      unwrap(
        getApi().GET('/api/database/data-sync/{data_sync_id}/properties/', {
          params: { path: { data_sync_id: dataSyncId } },
        }),
      ),
  })
}

export function useDataSyncProperties(dataSyncId: number) {
  return useQuery(dataSyncPropertiesQuery(dataSyncId))
}

/**
 * Changing the settings, or which of the source's properties are synced.
 *
 * **The answer is not written back.** `GET` serialises the sync through its own
 * type -- connection settings included -- while `PATCH` answers with the base
 * serializer, which has none of them. Storing that answer would empty the form
 * of everything it had just saved. So the sync is asked for again, which also
 * settles what the server made of the settings.
 */
export function updateDataSyncMutation(
  queryClient: QueryClient,
  dataSyncId: number,
  tableId: number,
) {
  return mutationOptions({
    mutationFn: (body: Record<string, unknown>) =>
      unwrap(
        getApi().PATCH('/api/database/data-sync/{data_sync_id}/', {
          params: { path: { data_sync_id: dataSyncId } },
          // Cast at the boundary, as every polymorphic body is: the compiler
          // cannot know which member of the union an id names.
          body: body as never,
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.dataSync(dataSyncId) })
      // The columns can change with the properties, and the properties are
      // asked of the source again with the new settings.
      await queryClient.invalidateQueries({ queryKey: qk.dataSyncProperties(dataSyncId) })
      await queryClient.invalidateQueries({ queryKey: qk.fields(tableId) })
    },
  })
}

export function useUpdateDataSync(dataSyncId: number, tableId: number) {
  return useMutation(updateDataSyncMutation(useQueryClient(), dataSyncId, tableId))
}

/**
 * Fetching from the source now.
 *
 * A job, because it reads everything the source has and writes every row that
 * changed. Nothing here waits for it: the rows arrive over the socket the way
 * any other write does, and what the job is for is saying how far along it is.
 */
export function syncNowMutation(queryClient: QueryClient, dataSyncId: number) {
  return mutationOptions({
    mutationFn: (): Promise<Job> =>
      unwrap(
        getApi().POST('/api/database/data-sync/{data_sync_id}/sync/async/', {
          params: { path: { data_sync_id: dataSyncId } },
        }),
      ) as Promise<Job>,
    onSuccess: (job) => {
      pushJob(queryClient, job)
    },
  })
}

export function useSyncNow(dataSyncId: number) {
  return useMutation(syncNowMutation(useQueryClient(), dataSyncId))
}

/**
 * A spreadsheet's sheets in Lithos Assets, and which can become a synced table.
 *
 * Read as the signed-in person, so a file they may not read answers as an error
 * here, before anything is created.
 */
export function assetsSheetsQuery(databaseId: number, fileId: number) {
  return queryOptions({
    queryKey: [...qk.database(databaseId), 'assets-sheets', fileId] as const,
    staleTime: Infinity,
    retry: false,
    queryFn: () =>
      unwrap(
        getApi().GET('/api/database/data-sync/assets/files/{file_id}/sheets/', {
          params: { path: { file_id: fileId }, query: { database_id: databaseId } },
        }),
      ),
  })
}

export function useAssetsSheets(databaseId: number, fileId: number) {
  return useQuery(assetsSheetsQuery(databaseId, fileId))
}

/**
 * Creating a table synced with one sheet of a spreadsheet in Lithos Assets.
 *
 * Every column the sheet has is synced, so the properties are asked for first;
 * the server then starts the table's first sync on its own.
 */
export function useCreateSpreadsheetSync(databaseId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      fileId: number
      sheetName: string
      tableName: string
    }) => {
      const settings = { assets_file_id: input.fileId, sheet_name: input.sheetName }
      const properties = await unwrap(
        getApi().POST('/api/database/data-sync/properties/', {
          body: { type: 'assets_spreadsheet', ...settings } as never,
        }),
      )
      return unwrap(
        getApi().POST('/api/database/data-sync/database/{database_id}/', {
          params: { path: { database_id: databaseId } },
          body: {
            type: 'assets_spreadsheet',
            table_name: input.tableName,
            synced_properties: properties.map((property) => property.key),
            ...settings,
          } as never,
        }),
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.tables(databaseId) })
    },
  })
}
